import { Box, CircularProgress, Paper, Table, TableBody, TableContainer, Tooltip, Grid, Typography} from "@mui/material";
import { useTranslation } from "react-i18next";
import { PriceDisplay } from "../../../../components/common/PriceDisplay";
import {
    StyledTableCell,
    StyledTableHead,
    StyledTableRow,
} from "../../../../components/table/BaseTable/BaseTable";
import {
    calculateAgreementPaymentPricing,
    isReservationPayment,
    isFinalSettlementPayment,
    usePaymentsForAgreement,
    useGetAgreementRescheduledPaymentList,
} from "../../../../services/agreementsService";
import { formatDecimalNumber } from "../../../../utils";
import { SendPaymentNotificationButton } from "../../SendPaymentNotificationButton";
import { USER_ROLES } from "../../../../helpers/constants";
import { useHasRole } from "../../../../hooks/auth/useCheckPermission";
import InfoIcon from "@mui/icons-material/Info";
import DateService from "../../../../services/dateService";


const PaymentInfoTooltip = (props) => {
    const { t } = useTranslation();

    return <>
  
        <Tooltip title={t("agreements.payment.paymentInfoTooltip")}>
            {"-"}
            <InfoIcon fontSize="small" sx={{marginLeft: "5px", fontSize: "20px"}}/>
        </Tooltip>
    </>
}

const RescheduledPaymentsTable = ({rescheduledPayments}) => {
    const { t } = useTranslation();
    
    const rescheduledPaymentRow = (payment) => {
        return (
            <StyledTableRow key={payment.id}>
                <StyledTableCell>{payment.tranche_name}</StyledTableCell>
                <StyledTableCell>{payment.agrepay_due_date || "-"}</StyledTableCell>
                <StyledTableCell>
                    <PriceDisplay price={payment.agreement_payment_amount} />
                </StyledTableCell>
                <StyledTableCell>
                    <PriceDisplay price={payment.paid_amount} />
                </StyledTableCell>
                <StyledTableCell>
                    <PriceDisplay price={payment.remaining_amount} />
                </StyledTableCell>
            </StyledTableRow>
        );
    } 

    const firstPayment = rescheduledPayments[0]


    return <Grid container >
        <Grid item xs={12} textAlign={"left"}>
            <Typography variant="h7"  style={{ margin: 0, color: "var(--primary)" }}>{`Aktualizacja z dnia ${DateService.convertDatetimeFromBackendToFormatDDMMYYYY(firstPayment.created)} przez ${firstPayment.creator}`}</Typography>
        </Grid>
        <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ width: "80%" }}>
                <Table sx={{ width: "100%" }}>
                    <StyledTableHead>
                        <StyledTableRow>
                            <StyledTableCell>{t("agreements.payment.payment")}</StyledTableCell>
                            <StyledTableCell>{t("agreements.payment.dueDate")}</StyledTableCell>
                            <StyledTableCell>{t("agreements.payment.amountGross")}</StyledTableCell>
                            <StyledTableCell>{t("agreements.payment.amountPaid")}</StyledTableCell>
                            <StyledTableCell>
                                {t("agreements.payment.amountRemaining")}
                            </StyledTableCell>
                        </StyledTableRow>
                    </StyledTableHead>
                    <TableBody>
                        {rescheduledPayments.map((payment) => {
                            return rescheduledPaymentRow(payment);
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    </Grid>
}


export const PaymentsTab = (props) => {
    const { agreement, readOnly } = props;
    const { t } = useTranslation();

    const { data: paymentsResponse, isLoading } = usePaymentsForAgreement(agreement.id);
    const { data: rescheduledPayments, isLoadingRescheduledPayments } = useGetAgreementRescheduledPaymentList({agreement_id: agreement.id});

    const isReservationAgreementAndHasRelatedAgreements = (
        agreement?.agreement_kind?.agrekind_key === "Reservation" &&
        (agreement?.development_agreement_id != null ||
        agreement?.preliminary_agreement_id != null ||
        agreement?.sale_agreement_id != null)
    )

    const payments = paymentsResponse?.results || [];
    const isAnyReservation = payments.some((payment) => isReservationPayment(payment));
    const nonReservationPayments = payments.filter((p) => !isReservationPayment(p));

    const hasPermissionToSendFinalSettlement = useHasRole([USER_ROLES.ADMIN, USER_ROLES.INVESTMENT_DPI])

    const paymentRow = (payment, paymentNumber) => {
        const paymentPricing = calculateAgreementPaymentPricing(agreement, payment);
        const isReservation = isReservationPayment(payment);
        const isFinalSettlement = isFinalSettlementPayment(payment);
        const name = isReservation
            ? t("agreements.payment.reservationPayment") :
            isFinalSettlement ? 
                t("agreements.payment.finalSettlementPayment") :
                `${t("agreements.payment.payment")} ${paymentNumber}`;
        return (
            <StyledTableRow key={payment.id}>
                <StyledTableCell>{name}</StyledTableCell>
                <StyledTableCell>{payment.agrepay_due_date || "-"}</StyledTableCell>
                <StyledTableCell>
                    <PriceDisplay price={paymentPricing.amountNet} />
                </StyledTableCell>
                <StyledTableCell>
                    <PriceDisplay price={paymentPricing.amountGross} />
                </StyledTableCell>
                {/*TODO DFG amount*/}
                <StyledTableCell>-</StyledTableCell>
                <StyledTableCell>
                    {Number(formatDecimalNumber(paymentPricing.totalPercentage) || 0).toFixed(2)}%
                </StyledTableCell>
                {/*TODO amount paid  */}
                <StyledTableCell>
                    {!isReservation && isReservationAgreementAndHasRelatedAgreements ? <PaymentInfoTooltip /> : <PriceDisplay price={payment.paid_amount} />}
                </StyledTableCell>
                {/*TODO amount remaining */}
                <StyledTableCell>
                    {!isReservation && isReservationAgreementAndHasRelatedAgreements ? <PaymentInfoTooltip /> : <PriceDisplay price={payment.remaining_amount} />}
                </StyledTableCell>
                <StyledTableCell>{payment.agrepay_notification_send_date || "-"}</StyledTableCell>
                <StyledTableCell>
                    {!isReservation && (
                        <SendPaymentNotificationButton
                            agreement={agreement}
                            disabled={readOnly || (isFinalSettlement && !hasPermissionToSendFinalSettlement)}
                            payment={payment}
                            paymentIdx={paymentNumber}
                            allPayments={nonReservationPayments}
                        />
                    )}
                </StyledTableCell>
            </StyledTableRow>
        );
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Grid container justifyContent={"flex-start"} rowGap={3}>
            <Grid item xs={1}/>
            <Grid item xs={10}>
                <TableContainer component={Paper} sx={{ width: "100%" }}>
                    <Table sx={{ width: "100%" }}>
                        <StyledTableHead>
                            <StyledTableRow>
                                <StyledTableCell>{t("agreements.payment.payment")}</StyledTableCell>
                                <StyledTableCell>{t("agreements.payment.dueDate")}</StyledTableCell>
                                <StyledTableCell>{t("agreements.payment.amountNet")}</StyledTableCell>
                                <StyledTableCell>{t("agreements.payment.amountGross")}</StyledTableCell>
                                <StyledTableCell>{t("agreements.payment.dfgAmount")}</StyledTableCell>
                                <StyledTableCell>
                                    {t("agreements.payment.totalPercentage")}
                                </StyledTableCell>
                                <StyledTableCell>{t("agreements.payment.amountPaid")}</StyledTableCell>
                                <StyledTableCell>
                                    {t("agreements.payment.amountRemaining")}
                                </StyledTableCell>
                                <StyledTableCell>{t("agreements.payment.sendDate")}</StyledTableCell>
                                <StyledTableCell>
                                    {t("agreements.payment.notification")}
                                </StyledTableCell>
                            </StyledTableRow>
                        </StyledTableHead>
                        <TableBody>
                            {payments.map((payment, idx) => {
                                const number = isAnyReservation ? idx : idx + 1;
                                return paymentRow(payment, number);
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
            <Grid item xs={1}/>
            {rescheduledPayments && rescheduledPayments.length > 0 && <>
                <Grid item xs={1}/>
                <Grid item xs={8}>
                    <RescheduledPaymentsTable rescheduledPayments={rescheduledPayments}/>
                </Grid></>}

        </Grid>
    );
};
